import type { BlogPostDraft } from '../../lib/blog';

export const post: BlogPostDraft = {
  slug: 'tableau-prospection-ugc',
  lang: 'fr',
  title: 'Tableau de prospection UGC : comment suivre ses marques et ses relances ?',
  description:
    'Apprends à suivre tes marques, tes relances et tes prochaines actions avec un tableau de prospection UGC simple et efficace.',
  date: '2026-09-04',
  author: 'Équipe Leadsme',
  tags: ['prospection', 'ugc', 'relances', 'collaborations'],
  ctaTitleKey: 'blog.postCtaOrganizeTitle',
  ctaDescriptionKey: 'blog.postCtaOrganizeDescription',
  ctaButtonKey: 'landing.ctaButton',
  ctaTrustKey: 'blog.postCtaOrganizeTrust',
  content: `
<p class="blog-lead">
  Contacter une marque prend quelques minutes. Se souvenir deux semaines plus tard du message envoyé, du nom de la personne contactée et de la date prévue pour la relance est souvent plus compliqué.
</p>
<p>
  Tant que tu prospectes occasionnellement, ta boîte mail ou tes messages Instagram peuvent sembler suffisants. Mais dès que plusieurs échanges avancent en parallèle, certaines opportunités finissent par être oubliées.
</p>
<p>
  Un tableau de prospection UGC permet de garder une vue claire sur chaque marque et, surtout, de savoir quelle action effectuer ensuite. Il ne doit pas devenir un fichier rempli d'informations inutiles&nbsp;: son rôle est de t'aider à transformer tes prises de contact en collaborations.
</p>
<p>
  Tu n'as pas encore constitué ta liste&nbsp;? Commence par découvrir nos <a href="/blog/trouver-marques-ugc">5&nbsp;pistes concrètes pour trouver des marques à contacter en UGC</a>.
</p>

<h2>À quoi sert un tableau de prospection UGC&nbsp;?</h2>
<p>
  Une marque peut être découverte sur Instagram, contactée par email, relancée sur LinkedIn puis revenir plusieurs semaines plus tard avec un brief. Sans suivi, les informations se retrouvent rapidement dispersées entre plusieurs outils.
</p>
<p>Ton tableau doit te permettre de répondre immédiatement à quatre questions&nbsp;:</p>
<ul>
  <li>Quelles marques souhaites-tu contacter&nbsp;?</li>
  <li>Lesquelles ont déjà reçu un message&nbsp;?</li>
  <li>Qui dois-tu relancer cette semaine&nbsp;?</li>
  <li>Quelles discussions peuvent réellement devenir des collaborations&nbsp;?</li>
</ul>
<p>
  L'objectif n'est donc pas uniquement de conserver un historique. Chaque ligne doit t'indiquer une prochaine étape claire.
</p>

<h2>Quelles informations conserver pour chaque marque&nbsp;?</h2>
<p>
  Un tableau trop détaillé demande beaucoup de temps à remplir et finit souvent par être abandonné. Commence avec les informations dont tu as réellement besoin pour personnaliser ton approche et reprendre une conversation.
</p>
<p>Pour chaque prospect, tu peux conserver&nbsp;:</p>
<ul>
  <li>le nom de la marque</li>
  <li>son secteur ou son univers</li>
  <li>son site et ses réseaux sociaux utiles</li>
  <li>le nom et la fonction de ton interlocuteur</li>
  <li>son adresse email ou le canal de contact utilisé</li>
  <li>l'idée de contenu que tu souhaites proposer</li>
  <li>la date de ton premier message</li>
  <li>la date du dernier échange</li>
  <li>le statut actuel de l'opportunité</li>
  <li>la prochaine action et sa date</li>
</ul>
<p>
  Ajoute une zone de notes courte pour conserver les éléments importants&nbsp;: un lancement à venir, le budget évoqué, une période pendant laquelle la marque souhaite être recontactée ou le nom d'une agence partenaire.
</p>
<p>
  Évite en revanche d'y recopier l'intégralité de chaque email. Quelques informations précises doivent suffire pour retrouver le contexte.
</p>
<p>Une entrée peut rester très simple&nbsp;:</p>
<blockquote>
  <p>Marque&nbsp;: Maison Aurore<br />
  Contact&nbsp;: Léa, Social Media Manager<br />
  Idée proposée&nbsp;: une routine du matin autour du produit<br />
  Premier contact&nbsp;: 3&nbsp;septembre par email<br />
  Statut&nbsp;: contactée<br />
  Prochaine action&nbsp;: relancer le 11&nbsp;septembre<br />
  Note&nbsp;: lancement d'une nouvelle gamme en octobre</p>
</blockquote>
<p>
  Cet exemple contient suffisamment d'informations pour personnaliser la relance et reprendre l'échange, sans transformer le suivi en compte rendu détaillé.
</p>

<h2>Quels statuts utiliser dans son suivi&nbsp;?</h2>
<p>
  Le statut permet de comprendre rapidement où se trouve chaque marque dans ta prospection. Choisis des étapes simples, avec une signification différente pour chacune.
</p>
<ul>
  <li><strong>À contacter</strong>&nbsp;: la marque correspond à ton profil, mais aucun message n'a encore été envoyé.</li>
  <li><strong>Contactée</strong>&nbsp;: ton premier email ou message a été envoyé.</li>
  <li><strong>À relancer</strong>&nbsp;: le délai que tu t'étais fixé est passé sans nouvelle réponse.</li>
  <li><strong>En discussion</strong>&nbsp;: la marque a répondu et vous échangez sur un besoin potentiel.</li>
  <li><strong>Proposition envoyée</strong>&nbsp;: un tarif, un devis ou une offre précise est en attente de validation.</li>
  <li><strong>Gagnée</strong>&nbsp;: la collaboration est confirmée.</li>
  <li><strong>Refusée ou à recontacter</strong>&nbsp;: l'opportunité n'aboutit pas maintenant, mais son historique reste utile.</li>
</ul>
<p>
  Évite les statuts trop vagues comme «&nbsp;en cours&nbsp;». Ils ne permettent pas de savoir si tu dois attendre, répondre ou relancer.
</p>
<p>
  Deux marques peuvent aussi avoir le même statut tout en demandant des actions différentes. Pour l'une, tu dois envoyer ton portfolio. Pour l'autre, attendre une validation budgétaire jusqu'à la fin du mois. Associe donc chaque opportunité à une action et, lorsque c'est nécessaire, à une date précise.
</p>
<p>
  Une marque qui te demande de revenir au moment de sa prochaine campagne n'est pas forcément perdue. Note directement une période de reprise de contact afin que cette information ne reste pas oubliée dans un email.
</p>

<h2>Comment planifier ses relances&nbsp;?</h2>
<p>
  La date de relance peut être ajoutée dès l'envoi du premier message. Si la marque répond avant, il suffit de la modifier ou de la supprimer.
</p>
<p>
  Après une prospection sans réponse, tu peux généralement prévoir une première relance cinq à sept jours ouvrés plus tard. Le délai reste à adapter à la situation, notamment lorsque ton idée concerne un lancement ou une période saisonnière.
</p>
<p>
  Note également la nature du message envoyé. Tu éviteras ainsi de renvoyer exactement la même proposition ou de demander une information que la marque t'a déjà transmise.
</p>
<p>Ta prochaine action peut être très concrète&nbsp;:</p>
<ul>
  <li>envoyer le premier email mardi</li>
  <li>relancer dans sept jours</li>
  <li>préparer trois idées de vidéos avant vendredi</li>
  <li>transmettre le devis après réception du brief</li>
  <li>reprendre contact au lancement de la collection d'hiver</li>
</ul>
<p>
  À l'ouverture de ton suivi, tu n'as plus besoin de relire toutes tes conversations pour savoir par où commencer.
</p>
<p>
  Pour choisir le bon timing et préparer ton message, retrouve notre <a href="/blog/relancer-marque-ugc">guide pour relancer une marque en UGC sans paraître insistant·e</a>.
</p>
<p>
  Si tu dois encore préparer ton premier contact, tu peux aussi t'appuyer sur nos <a href="/blog/email-prospection-ugc">7&nbsp;exemples d'emails de prospection UGC</a> en les adaptant à la marque.
</p>

<h2>Quand transformer un prospect en projet&nbsp;?</h2>
<p>
  Une marque intéressée n'est pas encore un projet confirmé. Tant que le contenu, le tarif et les principales conditions ne sont pas acceptés, conserve-la dans ta prospection.
</p>
<p>Lorsque la collaboration est validée, crée un suivi distinct pour la production. Tu pourras alors noter&nbsp;:</p>
<ul>
  <li>les contenus et les formats attendus</li>
  <li>la date de réception du produit</li>
  <li>les échéances de tournage et de livraison</li>
  <li>le nombre de modifications prévues</li>
  <li>le tarif accepté et les éventuels suppléments</li>
  <li>la durée des droits d'utilisation</li>
  <li>la date de facturation et l'échéance du paiement</li>
</ul>
<p>
  Séparer les prospects des projets évite de mélanger une marque simplement contactée avec une collaboration qui demande maintenant des livrables et des échéances précises.
</p>
<p>
  Si tu dois encore chiffrer la collaboration, consulte notre <a href="/blog/tarifs-ugc">guide consacré aux tarifs UGC et aux droits d'utilisation</a>.
</p>

<h2>Quels chiffres suivre pour améliorer sa prospection&nbsp;?</h2>
<p>
  Un tableau peut aussi t'aider à comprendre ce qui fonctionne. Il n'est pas nécessaire de créer un rapport complexe&nbsp;: quelques indicateurs suffisent.
</p>
<ul>
  <li>Le nombre de marques contactées chaque mois</li>
  <li>Le nombre de réponses obtenues</li>
  <li>Le nombre de discussions réellement qualifiées</li>
  <li>Le nombre de propositions envoyées</li>
  <li>Le nombre de collaborations signées</li>
  <li>Les secteurs et les canaux qui apportent le plus de réponses</li>
</ul>
<p>
  Ces chiffres doivent surtout t'aider à ajuster ta méthode. Si tu envoies beaucoup de messages mais obtiens peu de réponses, retravaille peut-être ta sélection de marques, ton objet d'email ou la personnalisation de ta proposition.
</p>
<p>
  À l'inverse, si plusieurs marques répondent mais qu'aucune collaboration n'est signée, le point de blocage se trouve peut-être dans ton offre, tes tarifs ou la manière dont tu présentes tes livrables.
</p>

<h2>Tableur, Notion ou CRM UGC&nbsp;: que choisir&nbsp;?</h2>
<p>
  Un tableur peut suffire lorsque tu débutes et que tu suis seulement quelques marques. Il est rapide à créer, facile à personnaliser et permet de tester les informations dont tu as réellement besoin.
</p>
<p>
  Notion permet d'ajouter différentes vues et de relier plusieurs bases, mais demande généralement un peu plus de configuration. L'outil le plus complet n'est pas forcément le meilleur si tu passes davantage de temps à l'organiser qu'à prospecter.
</p>
<p>
  Lorsque les contacts, les projets, les paiements et les échéances se multiplient, un CRM conçu pour ton activité permet de centraliser le suivi et d'éviter de maintenir plusieurs fichiers séparés.
</p>
<p>
  Le bon outil est celui que tu peux mettre à jour rapidement après chaque échange et consulter régulièrement sans devoir tout reconstruire.
</p>

<h2>La routine de 15&nbsp;minutes pour garder son suivi à jour</h2>
<p>
  Réserve un court moment chaque semaine pour revoir ta prospection. Pendant cette session, tu peux&nbsp;:
</p>
<ul>
  <li>traiter les relances arrivées à échéance</li>
  <li>mettre à jour les statuts après les dernières réponses</li>
  <li>ajouter les nouvelles marques repérées</li>
  <li>planifier une prochaine action pour chaque discussion active</li>
  <li>classer les opportunités terminées</li>
</ul>
<p>
  Mets également ton suivi à jour dès qu'une marque te répond. Cette habitude prend moins d'une minute et évite d'essayer de reconstituer plusieurs conversations en fin de semaine.
</p>
<p>
  Pour que ton tableau reste réellement utile, évite d'accumuler des centaines de marques sans vérifier leur pertinence, de conserver des statuts trop vagues ou d'ajouter des colonnes que tu ne consultes jamais.
</p>
<p>
  Ton système doit rester suffisamment simple pour être utilisé après chaque échange. Un suivi imparfait mais régulièrement mis à jour sera toujours plus utile qu'un modèle très complet abandonné après deux semaines.
</p>

<h2>Comment organiser sa prospection avec Leadsme&nbsp;?</h2>
<p>
  Avec Leadsme, tu peux centraliser les marques que tu souhaites contacter, retrouver les informations importantes de chaque échange et suivre l'avancement de tes opportunités au même endroit.
</p>
<p>
  Lorsqu'une collaboration est confirmée, tu peux également suivre le projet, les montants facturés et les paiements attendus sans créer un nouveau fichier pour chaque étape.
</p>
<p>
  <a href="https://leadsme.app">Créer gratuitement mon espace Leadsme</a>
</p>

<h2>Pour résumer</h2>
<p>
  Un bon tableau de prospection UGC ne sert pas à collectionner des noms de marques. Il doit t'indiquer où en est chaque échange et quelle action réaliser ensuite.
</p>
<p>
  Commence avec quelques informations essentielles&nbsp;: le contact, l'idée proposée, les dates importantes, le statut et la prochaine action. Ajoute davantage de détails uniquement lorsqu'ils t'aident réellement à prendre une décision ou à reprendre la conversation.
</p>
<p>
  Enfin, sépare la prospection des projets confirmés et prends quelques minutes chaque semaine pour mettre ton suivi à jour. Tu éviteras les relances oubliées et garderas une vision plus claire des opportunités qui peuvent devenir de véritables collaborations.
</p>
`,
};
